// FILE: src/services/paceInterventions.js
//
// Pure decision logic for Intelligent Pace Coaching interventions (Phase 3).
// Given the prior intervention state and the current inputs, decide which
// single coaching message (if any) to show, and return the next state. No
// React, no timers, no DOM — the usePaceInterventions hook holds the state in
// a ref and handles the message lifecycle around this reducer.
//
// Priority (highest first): time-sink → recovery → status transition →
// milestone. All message types share a frequency floor so the learner is
// never shown two messages within minSecondsBetweenMessages. Each message
// fires at most once per trigger (per transition / per milestone / per
// question). Design: docs/pace-coaching-proposal.md §4.

import { PACE_MESSAGES } from '../config/paceMessages';

/** Classify which milestone position answeredCount lands on, or null. */
export function milestonePhase(answeredCount, milestones) {
  if (!Array.isArray(milestones)) return null;
  const idx = milestones.indexOf(answeredCount);
  if (idx === -1) return null;
  if (idx === 0) return 'first';
  if (idx === milestones.length - 1) return 'finalStretch';
  return 'midway';
}

/** Fresh, serializable intervention state. */
export function createInterventionState() {
  return {
    lastStatus: null,       // previous pace-status band
    lastDelta: null,        // previous deltaSeconds (for recovery)
    firedMilestones: [],    // answeredCount values already announced
    timeSinkKey: null,      // question key that already fired a time-sink nudge
    lastShownAt: -Infinity, // ms timestamp of the last shown message
  };
}

/**
 * Decide the next coaching message.
 * @param {object} state  prior state (see createInterventionState)
 * @param {object} input  { now, status, deltaSeconds, answeredCount,
 *                          currentQuestionSeconds, currentQuestionKey, milestones }
 * @param {object} config { minSecondsBetweenMessages, timeSinkSeconds, recoveryMarginSeconds }
 * @returns {{ message: {type,text}|null, state: object }}
 */
export function nextPaceMessage(state, input, config = {}) {
  const floorMs = (config.minSecondsBetweenMessages ?? 90) * 1000;
  const timeSinkSeconds = config.timeSinkSeconds ?? 180;
  const recoveryMargin = config.recoveryMarginSeconds ?? 120;

  const within = (input.now - state.lastShownAt) < floorMs;
  const out = { ...state, firedMilestones: [...state.firedMilestones] };
  let message = null;

  // 1) Time-sink — most local/actionable. Once per question key.
  if (
    input.currentQuestionSeconds >= timeSinkSeconds &&
    input.currentQuestionKey != null &&
    state.timeSinkKey !== input.currentQuestionKey
  ) {
    if (!within) {
      message = { type: 'timeSink', text: PACE_MESSAGES.timeSink };
      out.timeSinkKey = input.currentQuestionKey;
    }
  }

  // 2) Recovery — was behind, has climbed back up by the margin into ok.
  const wasBehind = state.lastStatus === 'behind' || state.lastStatus === 'significantlyBehind';
  const nowOk = input.status === 'onTrack' || input.status === 'ahead';
  const improved = state.lastDelta != null && (input.deltaSeconds - state.lastDelta) >= recoveryMargin;
  if (!message && wasBehind && nowOk && improved && !within) {
    message = { type: 'recovery', text: PACE_MESSAGES.recovery };
  }

  // 3) Status transition — into a worse band, or into ahead.
  if (!message && input.status !== state.lastStatus) {
    let text = null;
    if (input.status === 'significantlyBehind') text = PACE_MESSAGES.status.significantlyBehind;
    else if (input.status === 'behind') text = PACE_MESSAGES.status.behind;
    else if (input.status === 'ahead') text = PACE_MESSAGES.status.ahead;
    if (text && !within) message = { type: input.status, text };
  }

  // 4) Milestone checkpoint — once per milestone position.
  if (!message) {
    const phase = milestonePhase(input.answeredCount, input.milestones);
    if (phase && !state.firedMilestones.includes(input.answeredCount) && !within) {
      const behindAtCheckpoint = input.status === 'behind' || input.status === 'significantlyBehind';
      const text = behindAtCheckpoint ? PACE_MESSAGES.milestone.behind : PACE_MESSAGES.milestone[phase];
      message = { type: 'milestone', text };
      out.firedMilestones.push(input.answeredCount);
    }
  }

  // Always advance the band/delta trackers so transitions are detected next time.
  out.lastStatus = input.status;
  out.lastDelta = input.deltaSeconds;
  if (message) out.lastShownAt = input.now;

  return { message, state: out };
}
